const post = {
  meta: {
    slug: "business-credit-vs-personal-credit",
    title: "Business Credit vs. Personal Credit: What Lenders Really Look At",
    excerpt:
      "Your business has its own credit file. So do you. Here's how lenders weigh both — and the one number that matters more than either.",
    date: "2025-11-12",
    readTime: "5 min read",
    category: "Credit",
    image:
      "https://images.pexels.com/photos/33866469/pexels-photo-33866469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  body: `
## Two credit files, one applicant

Every U.S. business owner has, at minimum, four credit files being watched by lenders:

1. **Personal FICO** — the 300–850 score you already know
2. **Personal credit report** — the underlying history behind the FICO
3. **Business credit** — held at Experian Business, Dun & Bradstreet, and Equifax Small Business
4. **Bank statement history** — technically not a credit file, but weighted more heavily than any of the above by most modern lenders

Applying for capital without knowing what's in each of these is like walking into a job interview without knowing what's on your résumé.

## Personal FICO: the gatekeeper

Fair or not, your personal FICO opens or closes most doors:

- **720+**: every product on the market, best pricing available
- **680–719**: SBA territory, bank lines of credit, prime fintech term loans
- **640–679**: fintech term loans, most equipment financing, some SBA
- **580–639**: revenue-based financing, specialty equipment
- **500–579**: MCA territory, hard collateralized loans only
- **Below 500**: very limited options — usually collateral or a co-signer

**Two credit tips that move the needle in 60 days:**
- Pay down revolving balances to under 30% of the limit. Utilization is a huge score input.
- Do not open new personal accounts before applying. Each hard inquiry costs 3–7 points and stays for 24 months.

## Business credit: the quiet score

Most owners have no idea their business has its own credit report until a lender mentions it. It's built out of:

- Trade lines (vendors who report your payment history — Uline, Grainger, Home Depot Pro, Quill, etc.)
- Business credit cards
- Business loans and lines of credit
- Public records (liens, judgments, bankruptcies against the entity)

The two most-watched business credit scores:

- **Experian Intelliscore** — 1–100, higher is better; 76+ is prime
- **D&B PAYDEX** — 1–100, tracks how promptly you pay vendors; 80 = paying on time, 100 = paying early

**How to build it fast:**
1. Get a D-U-N-S number (free at D&B)
2. Open a business credit card in the business's name (Amex, Chase Ink, Capital One Spark)
3. Open two or three net-30 vendor accounts that report (Uline, Grainger, Quill) — pay them early
4. Keep a business bank account with 12+ months of history and no NSFs

This all takes 6–12 months to build meaningfully, so start now if you haven't.

## Bank statements: the number modern lenders weight most

Here's the piece most owners underestimate. In modern underwriting — especially fintech — your **bank statements outweigh both credit files combined.**

Why? Because credit scores describe your past. Bank statements describe your present. Deposits, day-count, ending daily balances, negative days — these are running proof of whether you can service a payment, right now.

A business owner with a 640 FICO and $80K/month in clean deposits will beat an owner with a 750 FICO and $18K/month in choppy deposits, every time. The 640 owner has money. The 750 owner has a score.

## What lenders actually weigh

For a **traditional bank / SBA loan**, roughly:
- Personal FICO: 35%
- Tax returns / profitability: 30%
- Time in business: 15%
- Collateral: 10%
- Business credit: 10%

For a **fintech term loan or line of credit**, roughly:
- Bank statements: 45%
- Personal FICO: 25%
- Time in business: 15%
- Business credit: 10%
- Industry: 5%

For a **revenue advance / MCA**, roughly:
- Bank statements: 65%
- Time in business: 15%
- Personal FICO: 10%
- Industry: 5%
- Prior advance history: 5%

## Practical takeaway

If you're borrowing in the next 90 days, spend an hour on each of the following, in this order:

1. **Pull your personal FICO** from a real source (not Credit Karma's VantageScore — get the actual FICO). Note anything wrong.
2. **Download your last 4 months of business bank statements.** Look at them the way a lender would: how many deposits, average size, any negative days.
3. **Pull your business credit** (Experian and D&B both offer this — some for free with a $10–$50 credit).
4. **Fix the two or three quickest things** — dispute an error, pay down a maxed card, add a vendor line, close out an NSF cycle.

Small businesses that do this before applying beat identical businesses that don't. Every time.
`,
};

export default post;
